document.documentElement.classList.add("sd-js");

const initHeroCarousels = () => {
  document.querySelectorAll("[data-sd-hero-carousel]").forEach((carousel) => {
    const slides = Array.from(carousel.querySelectorAll("[data-sd-hero-slide]"));
    const dots = Array.from(carousel.querySelectorAll("[data-sd-hero-dot]"));
    if (slides.length < 2) return;

    let index = 0;
    let timer;

    const activate = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle("is-active", slideIndex === index);
      });
      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle("is-active", isActive);
        if (isActive) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });
    };

    const start = () => {
      window.clearInterval(timer);
      timer = window.setInterval(() => activate(index + 1), 4000);
    };

    dots.forEach((dot, dotIndex) => {
      dot.addEventListener("click", () => {
        activate(dotIndex);
        start();
      });
    });

    carousel.addEventListener("mouseenter", () => window.clearInterval(timer));
    carousel.addEventListener("mouseleave", start);
    start();
  });
};

const initProductRails = () => {
  document.querySelectorAll("[data-sd-rail]").forEach((rail) => {
    const track = rail.querySelector("[data-sd-rail-track]");
    const previous = rail.querySelector("[data-sd-rail-prev]");
    const next = rail.querySelector("[data-sd-rail-next]");
    if (!track || !previous || !next) return;

    const scrollByCard = (direction) => {
      const firstCard = track.querySelector(".sd-product-card");
      const amount = firstCard
        ? firstCard.getBoundingClientRect().width + 20
        : track.clientWidth * 0.85;
      track.scrollBy({ left: amount * direction, behavior: "smooth" });
    };

    const updateButtons = () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 4;
      previous.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= maxScroll;
      rail.classList.toggle("has-overflow", track.scrollWidth > track.clientWidth + 4);
    };

    previous.addEventListener("click", () => scrollByCard(-1));
    next.addEventListener("click", () => scrollByCard(1));
    track.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);
    updateButtons();
  });
};

const initPredictiveSearch = () => {
  document.querySelectorAll("[data-sd-search-form]").forEach((form) => {
    const input = form.querySelector("[data-sd-search-input]");
    const results = form.querySelector("[data-sd-search-results]");
    if (!input || !results) return;

    let controller;

    const escapeHtml = (value = "") =>
      value
        .toString()
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const renderEmpty = (message) => {
      results.innerHTML = `<p>${message}</p>`;
      results.classList.add("is-visible");
    };

    const renderProducts = (products) => {
      if (!products.length) {
        renderEmpty("No product suggestions found.");
        return;
      }

      results.innerHTML = products
        .map((product) => {
          const image = product.image || product.featured_image || {};
          const imageUrl = typeof image === "string" ? image : image.url;
          const title = escapeHtml(product.title);
          const url = escapeHtml(product.url);
          const imageMarkup = imageUrl
            ? `<img src="${escapeHtml(imageUrl)}" alt="${title}" loading="lazy">`
            : "";
          return `
            <a href="${url}">
              ${imageMarkup}
              <span>${title}</span>
            </a>
          `;
        })
        .join("");
      results.classList.add("is-visible");
    };

    input.addEventListener("input", async () => {
      const query = input.value.trim();
      if (controller) controller.abort();

      if (query.length < 2) {
        results.innerHTML = "";
        results.classList.remove("is-visible");
        return;
      }

      controller = new AbortController();
      try {
        const response = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error("Search unavailable");
        const data = await response.json();
        renderProducts(data.resources?.results?.products || []);
      } catch (error) {
        if (error.name !== "AbortError") renderEmpty("Search suggestions unavailable.");
      }
    });

    document.addEventListener("click", (event) => {
      if (!form.contains(event.target)) results.classList.remove("is-visible");
    });
  });
};

const initHeaderPopovers = () => {
  const popovers = Array.from(
    document.querySelectorAll(".sd-header__search-popover, .sd-header__account-popover")
  );
  if (!popovers.length) return;

  document.addEventListener("click", (event) => {
    popovers.forEach((popover) => {
      if (!popover.contains(event.target)) popover.removeAttribute("open");
    });
  });

  popovers.forEach((popover) => {
    popover.addEventListener("toggle", () => {
      if (!popover.open) return;
      popovers.forEach((other) => {
        if (other !== popover) other.removeAttribute("open");
      });
    });
  });
};

const initAjaxCart = () => {
  const drawer = document.querySelector("[data-sd-cart-drawer]");
  if (!drawer) return;

  const itemsContainer = drawer.querySelector("[data-sd-cart-drawer-items]");
  const countText = drawer.querySelector("[data-sd-cart-drawer-count]");
  const totalText = drawer.querySelector("[data-sd-cart-drawer-total]");
  const message = drawer.querySelector("[data-sd-cart-message]");
  const panel = drawer.querySelector(".sd-cart-drawer__panel");
  const countBadges = Array.from(document.querySelectorAll(".sd-cart-count"));
  const productPlaceholder = drawer.dataset.sdProductPlaceholder || "";

  const escapeHtml = (value = "") =>
    value
      .toString()
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatMoney = (cents = 0) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP"
    }).format(Number(cents || 0) / 100);

  const setMessage = (text, type = "info") => {
    if (!message) return;
    message.textContent = text;
    message.dataset.state = type;
  };

  const itemCountLabel = (count) => `${count} ${count === 1 ? "item" : "items"}`;

  const openDrawer = () => {
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.classList.add("sd-cart-drawer-open");
    panel?.focus?.();
  };

  const closeDrawer = () => {
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.classList.remove("sd-cart-drawer-open");
  };

  const renderProperties = (properties = {}) => {
    const rows = Object.entries(properties).filter(
      ([key, value]) => key.charAt(0) !== "_" && value !== null && value !== ""
    );
    if (!rows.length) return "";
    return `<dl class="sd-cart-drawer-item__properties">${rows
      .map(([key, value]) => `<div><dt>${escapeHtml(key)}:</dt><dd>${escapeHtml(value)}</dd></div>`)
      .join("")}</dl>`;
  };

  const renderItem = (item) => {
    const isServiceLine = item.properties?._sports_devil_service;
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
      : `<img src="${escapeHtml(productPlaceholder)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`;
    const variantTitle =
      item.variant_title && item.variant_title !== "Default Title"
        ? `<span>${escapeHtml(item.variant_title)}</span>`
        : "";
    const nextDown = Math.max(0, Number(item.quantity || 0) - 1);
    const nextUp = Number(item.quantity || 0) + 1;

    return `
      <article class="sd-cart-drawer-item">
        <a class="sd-cart-drawer-item__media" href="${escapeHtml(item.url)}">${image}</a>
        <div class="sd-cart-drawer-item__main">
          <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${variantTitle}
          ${renderProperties(item.properties)}
          ${
            isServiceLine
              ? `<p class="sd-cart-drawer-item__service">Service quantity follows the selected bat.</p>`
              : `<div class="sd-cart-drawer-item__qty">
                  <button type="button" data-sd-cart-change="${escapeHtml(item.key)}" data-sd-cart-quantity="${nextDown}" aria-label="Decrease ${escapeHtml(item.product_title)}">-</button>
                  <strong>${item.quantity}</strong>
                  <button type="button" data-sd-cart-change="${escapeHtml(item.key)}" data-sd-cart-quantity="${nextUp}" aria-label="Increase ${escapeHtml(item.product_title)}">+</button>
                </div>`
          }
        </div>
        <div class="sd-cart-drawer-item__aside">
          <strong>${formatMoney(item.final_line_price)}</strong>
          <button type="button" data-sd-cart-change="${escapeHtml(item.key)}" data-sd-cart-quantity="0">Remove</button>
        </div>
      </article>
    `;
  };

  const renderCart = (cart) => {
    countBadges.forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.classList.toggle("is-empty", cart.item_count === 0);
    });
    if (countText) countText.textContent = itemCountLabel(cart.item_count);
    if (totalText) totalText.textContent = formatMoney(cart.items_subtotal_price);

    if (!itemsContainer) return;
    if (!cart.items?.length) {
      itemsContainer.innerHTML = `
        <div class="sd-cart-drawer__empty">
          <strong>Your cart is empty.</strong>
          <p>Add cricket, hockey, rugby, and training kit from the catalogue.</p>
        </div>
      `;
      return;
    }

    itemsContainer.innerHTML = cart.items.map(renderItem).join("");
  };

  const fetchCart = async ({ open = false, statusText = "" } = {}) => {
    const response = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    });
    if (!response.ok) throw new Error("Cart could not be loaded.");
    const cart = await response.json();
    renderCart(cart);
    if (statusText) setMessage(statusText, "success");
    if (open) openDrawer();
    return cart;
  };

  const parseCartError = async (response) => {
    try {
      const data = await response.json();
      return data.description || data.message || "Cart update failed.";
    } catch {
      return "Cart update failed.";
    }
  };

  const formPropertyName = /^properties\[(.+)]$/;

  const buildPropertiesFromFormData = (formData) => {
    const properties = {};
    formData.forEach((value, key) => {
      const match = key.match(formPropertyName);
      if (!match) return;
      const cleanValue = String(value || "").trim();
      if (!cleanValue || cleanValue === "0") return;
      properties[match[1]] = cleanValue;
    });
    return properties;
  };

  const selectedAddonItems = (form, formData) => {
    const mainVariantId = formData.get("id");
    const productQuantity = Math.max(1, Number.parseInt(formData.get("quantity") || "1", 10) || 1);
    const parentProductTitle = form.dataset.sdProductTitle || "";
    const items = [];

    form.querySelectorAll("[data-sd-addon-variant-id]").forEach((control) => {
      const variantId = control.dataset.sdAddonVariantId;
      if (!variantId) return;

      let addonQuantity = 0;
      if (control.matches('input[type="checkbox"]')) {
        addonQuantity = control.checked ? 1 : 0;
      } else if (control.matches('input[type="number"]')) {
        addonQuantity = Math.max(0, Number.parseInt(control.value || "0", 10) || 0);
      }

      if (!addonQuantity) return;

      items.push({
        id: Number(variantId),
        quantity: addonQuantity * productQuantity,
        properties: {
          For: parentProductTitle,
          _sports_devil_service: "bat_customisation",
          _sports_devil_parent_variant: String(mainVariantId || ""),
          _sports_devil_addon: control.dataset.sdAddonTitle || "Bat customisation"
        }
      });
    });

    return items;
  };

  const addForm = async (form, submitter) => {
    const button = submitter?.matches?.('button[type="submit"], input[type="submit"]')
      ? submitter
      : form.querySelector('button[type="submit"], input[type="submit"]');
    const originalText = button?.tagName === "BUTTON" ? button.textContent : null;

    if (button) {
      button.disabled = true;
      if (button.tagName === "BUTTON") button.textContent = "Adding...";
    }
    setMessage("Adding item...", "info");

    try {
      const formData = new FormData(form);
      const addons = selectedAddonItems(form, formData);
      const body = addons.length
        ? JSON.stringify({
            items: [
              {
                id: Number(formData.get("id")),
                quantity: Math.max(1, Number.parseInt(formData.get("quantity") || "1", 10) || 1),
                properties: buildPropertiesFromFormData(formData)
              },
              ...addons
            ]
          })
        : formData;
      const headers = addons.length
        ? { Accept: "application/json", "Content-Type": "application/json" }
        : { Accept: "application/json" };
      const response = await fetch("/cart/add.js", {
        method: "POST",
        body,
        headers,
        credentials: "same-origin"
      });

      if (!response.ok) {
        throw new Error(await parseCartError(response));
      }

      await fetchCart({ open: true, statusText: "Added to basket." });
    } catch (error) {
      setMessage(error.message || "Unable to add item. Open basket and try again.", "error");
      openDrawer();
    } finally {
      if (button) {
        button.disabled = false;
        if (button.tagName === "BUTTON" && originalText) button.textContent = originalText;
      }
    }
  };

  const changeLine = async (key, quantity) => {
    setMessage("Updating basket...", "info");
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      credentials: "same-origin",
      body: JSON.stringify({ id: key, quantity: Number(quantity) })
    });
    if (!response.ok) throw new Error(await parseCartError(response));
    const cart = await response.json();
    renderCart(cart);
    setMessage("Basket updated.", "success");
  };

  document.addEventListener("submit", (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || form.dataset.sdNoAjax === "true") return;
    event.preventDefault();
    addForm(form, event.submitter);
  });

  document.addEventListener("click", (event) => {
    const cartTrigger = event.target.closest("[data-sd-cart-open]");
    if (cartTrigger && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      fetchCart({ open: true }).catch(() => {
        window.location.href = cartTrigger.href || "/cart";
      });
      return;
    }

    if (event.target.closest("[data-sd-cart-close]")) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const change = event.target.closest("[data-sd-cart-change]");
    if (!change) return;
    event.preventDefault();
    changeLine(change.dataset.sdCartChange, change.dataset.sdCartQuantity).catch((error) => {
      setMessage(error.message || "Basket update failed.", "error");
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && drawer.classList.contains("is-open")) closeDrawer();
  });
};

const initProductPages = () => {
  document.querySelectorAll("[data-sd-product]").forEach((product) => {
    const featuredImage = product.querySelector(".sd-product__featured-media img");
    const thumbs = Array.from(product.querySelectorAll("[data-sd-product-thumb]"));
    const mediaPrevious = product.querySelector("[data-sd-product-media-prev]");
    const mediaNext = product.querySelector("[data-sd-product-media-next]");
    const mediaCount = product.querySelector("[data-sd-product-media-count]");
    const variantSelect = product.querySelector("[data-sd-variant-select]");
    const variantRadios = product.querySelectorAll('input[type="radio"][name="id"]');
    const optionRadios = Array.from(product.querySelectorAll("[data-sd-option-position]"));
    const variantIdInput = product.querySelector("[data-sd-variant-id-input]");
    const variantJson = product.querySelector("[data-sd-product-variants-json]");
    const productForm = product.querySelector("[data-sd-product-form]");
    const submitButton = productForm?.querySelector('button[type="submit"]');
    const price = product.querySelector("[data-sd-product-price]");
    const total = product.querySelector("[data-sd-product-total]");
    const note = product.querySelector("[data-sd-variant-note]");
    const share = product.querySelector("[data-sd-share]");
    const fullPrepAddon = product.querySelector("[data-sd-addon-disables-full-prep-conflicts]");
    const addonControls = Array.from(product.querySelectorAll("[data-sd-addon-variant-id]"));
    const fullPrepConflicts = Array.from(
      product.querySelectorAll("[data-sd-addon-disabled-by-full-prep]")
    );
    const engravingToggle = product.querySelector("[data-sd-engraving-toggle]");
    const engravingField = product.querySelector("[data-sd-engraving-field]");
    const engravingInput = product.querySelector("[data-sd-engraving-input]");
    let selectedVariantPriceCents = Number.parseInt(product.dataset.sdBasePriceCents || "0", 10);
    const variants = (() => {
      if (!variantJson?.textContent) return [];
      try {
        return JSON.parse(variantJson.textContent);
      } catch {
        return [];
      }
    })();

    const formatMoney = (value) => {
      const amount = Number(value || 0) / 100;
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP"
      }).format(amount);
    };

    const imageFromVariant = (variant) => {
      if (!variant) return "";
      return (
        variant.featured_image?.src ||
        variant.featured_media?.preview_image?.src ||
        variant.image?.src ||
        ""
      );
    };

    const applyVariant = (control, label) => {
      if (!control) return;

      if (price) {
        const compare = control.dataset.compareAtPrice;
        price.innerHTML = `<span class="sd-price">${control.dataset.price || ""}${
          compare ? ` <s>${compare}</s>` : ""
        }</span>`;
      }

      if (total) {
        total.textContent = control.dataset.price || "";
      }

      if (featuredImage && control.dataset.image) {
        featuredImage.src = control.dataset.image;
        featuredImage.srcset = "";
      }

      if (note && label) {
        note.textContent = `Current selection: ${label.trim()}`;
      }

      if (control.dataset.url) {
        window.history.replaceState({}, "", control.dataset.url);
      }
    };

    const setUnavailableVariant = () => {
      if (variantIdInput) variantIdInput.value = "";
      if (note) note.textContent = "Current selection: Unavailable";
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = "Unavailable";
      }
      syncProductTotal();
    };

    const applyVariantObject = (variant) => {
      if (!variant) {
        setUnavailableVariant();
        return;
      }
      const priceText = formatMoney(variant.price);
      const compareText =
        variant.compare_at_price && variant.compare_at_price > variant.price
          ? formatMoney(variant.compare_at_price)
          : "";
      selectedVariantPriceCents = Number(variant.price || 0);

      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }

      if (price) {
        price.innerHTML = `<span class="sd-price">${priceText}${
          compareText ? ` <s>${compareText}</s>` : ""
        }</span>`;
      }

      const image = imageFromVariant(variant);
      if (featuredImage && image) {
        featuredImage.src = image;
        featuredImage.srcset = "";
      }

      if (note) {
        note.textContent = `Current selection: ${variant.title}`;
      }

      if (submitButton) {
        submitButton.disabled = !variant.available;
        submitButton.textContent = variant.available ? "Add to cart" : "Sold out";
      }

      if (variant.url) {
        window.history.replaceState({}, "", variant.url);
      }

      syncProductTotal();
    };

    const currentOptionSelection = () => {
      const selected = [];
      optionRadios.forEach((radio) => {
        if (!radio.checked) return;
        const position = Number.parseInt(radio.dataset.sdOptionPosition || "0", 10);
        selected[position - 1] = radio.value;
      });
      return selected;
    };

    const syncOptionVariant = () => {
      if (!optionRadios.length || !variants.length) return;
      const selected = currentOptionSelection();
      const variant = variants.find((item) =>
        (item.options || []).every((option, index) => option === selected[index])
      );
      applyVariantObject(variant);
    };

    const showMediaAt = (index) => {
      if (!featuredImage || !thumbs.length) return;
      const activeIndex = (index + thumbs.length) % thumbs.length;
      const thumb = thumbs[activeIndex];
      const image = thumb?.dataset.sdProductImage;
      if (!image) return;
      featuredImage.src = image;
      featuredImage.srcset = "";
      thumbs.forEach((item, itemIndex) => {
        item.classList.toggle("is-active", itemIndex === activeIndex);
      });
      if (mediaCount) mediaCount.textContent = `${activeIndex + 1} / ${thumbs.length}`;
    };

    const currentMediaIndex = () => {
      const activeIndex = thumbs.findIndex((thumb) => thumb.classList.contains("is-active"));
      return activeIndex === -1 ? 0 : activeIndex;
    };

    const addonQuantity = (control) => {
      if (control.matches('input[type="checkbox"]')) return control.checked ? 1 : 0;
      if (control.matches('input[type="number"]')) {
        return Math.max(0, Number.parseInt(control.value || "0", 10) || 0);
      }
      return 0;
    };

    const selectedAddonTotal = () =>
      addonControls.reduce((sum, control) => {
        const priceCents = Number.parseInt(control.dataset.sdAddonPriceCents || "0", 10) || 0;
        return sum + priceCents * addonQuantity(control);
      }, 0);

    function syncProductTotal() {
      if (!total) return;
      total.textContent = formatMoney(selectedVariantPriceCents + selectedAddonTotal());
    }

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener("click", () => {
        const image = thumb.dataset.sdProductImage;
        if (!featuredImage || !image) return;
        showMediaAt(index);
      });
    });

    mediaPrevious?.addEventListener("click", () => showMediaAt(currentMediaIndex() - 1));
    mediaNext?.addEventListener("click", () => showMediaAt(currentMediaIndex() + 1));

    if (variantSelect) {
      variantSelect.addEventListener("change", () => {
        const option = variantSelect.selectedOptions[0];
        applyVariant(option, option?.textContent || "");
      });
    }

    variantRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        if (!radio.checked) return;
        const label = radio.closest("label")?.innerText || "";
        applyVariant(radio, label);
      });
    });

    optionRadios.forEach((radio) => {
      radio.addEventListener("change", syncOptionVariant);
    });
    syncOptionVariant();

    const syncAddonConflicts = () => {
      if (!fullPrepAddon || !fullPrepConflicts.length) return;
      const fullPrepSelected = fullPrepAddon.checked;
      fullPrepConflicts.forEach((control) => {
        control.disabled = fullPrepSelected;
        control.closest("label")?.classList.toggle("is-disabled", fullPrepSelected);
        if (!fullPrepSelected) return;
        if (control.matches('input[type="checkbox"]')) control.checked = false;
        if (control.matches('input[type="number"]')) control.value = "0";
      });
      syncProductTotal();
    };

    const syncEngravingField = () => {
      if (!engravingToggle || !engravingField || !engravingInput) return;
      const selected = engravingToggle.checked;
      engravingField.hidden = !selected;
      engravingInput.required = selected;
      if (!selected) engravingInput.value = "";
    };

    fullPrepAddon?.addEventListener("change", syncAddonConflicts);
    addonControls.forEach((control) => {
      control.addEventListener("change", () => {
        syncProductTotal();
        syncEngravingField();
      });
      control.addEventListener("input", syncProductTotal);
    });
    syncAddonConflicts();
    syncEngravingField();
    syncProductTotal();

    if (share) {
      share.addEventListener("click", async () => {
        const shareData = { title: document.title, url: window.location.href };
        if (navigator.share) {
          await navigator.share(shareData).catch(() => {});
          return;
        }
        await navigator.clipboard?.writeText(window.location.href).catch(() => {});
      });
    }
  });
};

const initCartQuantityControls = () => {
  document.querySelectorAll("[data-sd-quantity-control]").forEach((control) => {
    const input = control.querySelector("[data-sd-qty-input]");
    const minus = control.querySelector("[data-sd-qty-minus]");
    const plus = control.querySelector("[data-sd-qty-plus]");
    if (!input || !minus || !plus) return;

    const update = (delta) => {
      const current = Number.parseInt(input.value || "0", 10);
      input.value = Math.max(0, current + delta).toString();
      input.dispatchEvent(new Event("change", { bubbles: true }));
    };

    minus.addEventListener("click", () => update(-1));
    plus.addEventListener("click", () => update(1));
  });
};

const initCollectionFilters = () => {
  document.querySelectorAll("[data-sd-filter-toggle]").forEach((toggle) => {
    const panelId = toggle.getAttribute("aria-controls");
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;

    toggle.addEventListener("click", () => {
      const isOpen = panel.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    });
  });
};

document.addEventListener("click", (event) => {
  const trigger = event.target.closest("[data-sd-scroll-to]");
  if (!trigger) return;

  const target = document.querySelector(trigger.getAttribute("data-sd-scroll-to"));
  if (!target) return;

  event.preventDefault();
  target.scrollIntoView({ behavior: "smooth", block: "start" });
});

document.addEventListener("DOMContentLoaded", () => {
  initHeroCarousels();
  initProductRails();
  initHeaderPopovers();
  initPredictiveSearch();
  initAjaxCart();
  initProductPages();
  initCartQuantityControls();
  initCollectionFilters();
});
